/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import ApiService from "./ApiService";
import Background from "model/character/Background";
import SkillStats from "model/stats/SkillStats";
import Utils from "utils/Utils";
import CharacterService from "./CharacterService";
import ItemOption from 'model/character/ItemOption';


class BackgroundService {

  static parseSkills(
    background: Background,
    skillOptions: ItemOption[],
    skillsSelected: string[]
  ): void {
    CharacterService.splitSkills(background.skill_proficiencies, background.name, skillOptions, skillsSelected);

    let profs = background.tool_proficiencies;
    if (profs === null || profs === "No additional tool proficiencies") {
      return;
    }

    const oring = profs.includes(' or ');
    profs = profs.replace(/,? or /gi, ', ')
      .replace(/one type of /gi, '')
      .replace(/Herbalism kit/gi, 'Herbalist kit')
      .replace(/Your choice of one from /gi, '')
      .replace(/ set of your choice/gi, '')
      .replace(/one /gi, '')
      .replace(/\./gi, '')
      .replace(/’/gi, "'")
      .toLowerCase();

    if (profs === 'two of your choice') {
      skillOptions.push({ label: `${background.name} (tool)`, options: [...SkillStats.tools] });
      skillOptions.push({ label: `${background.name} (tool)`, options: [...SkillStats.tools] });
      skillsSelected.push('');
      skillsSelected.push('');
    } else {
      let validTools: string[] = [];
      let types: string[] = [];
      for (const prof of profs.split(', ')) {
        let found = false;
        const profTrimmed = prof.trim();
        if (profTrimmed === "artisan's tools") {
          validTools.push(...SkillStats.tools);
          found = true;
          types.push('tool');
        } else if (profTrimmed === 'gaming set') {
          validTools.push(...SkillStats.gamingSets);
          found = true;
          types.push('game');
        } else if (profTrimmed === 'musical instrument') {
          validTools.push(...SkillStats.musicalInstruments);
          found = true;
          types.push('inst');
        } else {
          const tool = SkillStats.tools.find(tool => profTrimmed === tool.toLowerCase());
          if (tool) {
            validTools.push(tool);
            found = true;
            types.push('skill');
          }
        }
        if (!oring && found) {
          this.pushSkillOption(skillOptions, skillsSelected, background, validTools, types);
          validTools = [];
          types = [];
        }
      }
      if (validTools.length) {
        this.pushSkillOption(skillOptions, skillsSelected, background, validTools, types);
      }
    }
  }

  private static pushSkillOption(
    skillOptions: ItemOption[],
    skillsSelected: string[],
    background: Background,
    validTools: string[],
    types: string[]
  ): void {
    let label = background.name;
    if (types.length > 0) {
      const typeStr = types.join(', ');
      if (typeStr !== 'skill') {
        label += ` (${typeStr})`;
      }
    }
    skillOptions.push({ label, options: validTools });
    if (validTools.length === 1) {
      skillsSelected.push(validTools[0]);
    } else {
      skillsSelected.push('');
    }
  }

  static parseLanguages(
    background: Background,
    languageOptions: ItemOption[],
    languagesSelected: string[]
  ): void {
    const bgLanguage = background.languages?.toLowerCase().replace(/’/g, "'");
    const langs: string[][] = [];
    if (bgLanguage && bgLanguage !== 'no additional languages') {
      if (bgLanguage.startsWith('one language of your choice') ||
        bgLanguage.startsWith('one of your choice')) {
        langs.push([...Utils.langs]);
      } else if (bgLanguage === 'two of your choice') {
        langs.push([...Utils.langs]);
        langs.push([...Utils.langs]);
      } else {
        for (const lang of Utils.langs) {
          if (lang.toLowerCase() === bgLanguage) {
            langs.push([lang]);
          }
        }
      }
    }
    if (background.feature?.toLowerCase() === "thieves' cant") {
      languageOptions.push({ label: background.name, options: ["Thieves' cant"] });
      languagesSelected.push("Thieves' cant");
    }

    for (const langOpts of langs) {
      languageOptions.push({ label: background.name, options: langOpts });
      languagesSelected.push(langOpts.length === 1 ? langOpts[0] : '');
    }
  }


  static parseEquipment(background: Background,
                        equipmentOptions: ItemOption[],
                        equipmentSelected: string[]): void {

    const eqListOfOptions: string[][] = [];
    let equipment = background.equipment;
    if (equipment.startsWith('A dagger, quarterstaff, or spear, ')) {
      eqListOfOptions.push(['Dagger', 'Quarterstaff', 'Spear']);
      equipment = equipment.substring('A dagger, quarterstaff, or spear, '.length);
    }
    if (equipment.startsWith('A dagger, quarterstaff, or shortsword (if proficient), ')) {
      eqListOfOptions.push(['Dagger', 'Quarterstaff', 'Shortsword']);
      equipment = equipment.substring('A dagger, quarterstaff, or shortsword (if proficient), '.length);
    }
    if (equipment.startsWith('A club, handaxe, or spear, ')) {
      eqListOfOptions.push(['Club', 'Handaxe', 'Spear']);
      equipment = equipment.substring('A club, handaxe, or spear, '.length);
    }
    if (equipment.includes('a collection of bones, shells, or other natural objects')) {
      eqListOfOptions.push(['a collection of bones', 'a collection of shells', 'a collection of natural objects']);
      equipment = equipment.replace('a collection of bones, shells, or other natural objects', '');
    }
    if (equipment.includes('A book collecting all the songs, poems, or stories you know, ')) {
      eqListOfOptions.push(['A book collecting all the songs, poems, or stories you know']);
      equipment = equipment.replace('A book collecting all the songs, poems, or stories you know, ', '');
    }
    if (equipment.includes('a leather satchel containing perfume recipes, research notes, and chemical and botanical samples;')) {
      eqListOfOptions.push(['a leather satchel', 'perfume recipes', 'research notes', 'chemical samples', 'botanical samples']);
      equipment = equipment.replace('a leather satchel containing perfume recipes, research notes, and chemical and botanical samples;', '');
    }
    
    // Drop empty segments so a blank equipment string (e.g. Fate-Touched, which grants no starting
    // gear) doesn't produce a bogus single empty-option group — that renders as a disabled, red,
    // never-satisfiable select and blocks the equipment-completion check.
    const eqOptions: string[] = equipment.split(/,(?![^()]*\))/).map(s => s.trim()).filter(s => s.length > 0);
    eqOptions.forEach(eqOpt => {
      
      if (eqOpt === 'and a prayer book, prayer wheel, or prayer beads.') {
        eqListOfOptions.push(['Prayer Book', 'Prayer Wheel', 'Prayer Beads']);
      } else if (eqOpt === 'a prayer book or prayer wheel') {
        eqListOfOptions.push(['Prayer Book', 'Prayer Wheel']);
      } else if (eqOpt === 'tools for your typical con (such as bottles full of colored water, a set of weighted dice, a deck of marked or filed cards, or a seal from an imaginary-or real-noble)') {
        eqListOfOptions.push(['bottles full of colored water', 'a set of weighted dice', 'a deck of marked cards', 'a deck of filed cards', 'a seal from an imaginary noble', 'a seal from an real noble']);
      } else if (eqOpt === 'A set of artisan\'s tools of your choice' ||
                 eqOpt === 'One set of artisan\'s tools') {
        eqListOfOptions.push(SkillStats.artisanTools);
      } else if (eqOpt === 'Any artisan\'s tools except alchemist\'s supplies') {
        eqListOfOptions.push(SkillStats.artisanTools.filter(o => o.toLocaleLowerCase() !== 'alchemist\'s supplies' ));
      } else if (eqOpt === 'One set of artisan\'s tools or one instrument' ||
                 eqOpt === 'a set of artisan\'s tools or a musical instrument (one of your choice)') {
        eqListOfOptions.push([...SkillStats.artisanTools, ...SkillStats.musicalInstruments]);
      } else if (eqOpt === 'Lute or other musical instrument' ||
                 eqOpt === 'one musical instrument of your choice' ||
                 eqOpt === 'a musical instrument of your choice') {
        eqListOfOptions.push(SkillStats.musicalInstruments);
      } else if (eqOpt === 'a memento of your destiny (a keepsake from your betrothed, the seal of your destined office, your family signet ring, or similar)') {
        eqListOfOptions.push(['a keepsake from your betrothed', 'the seal of your destined office', 'your family signet ring', 'a memento of your destiny']);
      } else if (eqOpt === 'A set of either brewer\'s supplies or cook\'s utensils') {
        eqListOfOptions.push(['Brewer\'s supplies', 'Cook\'s utensils']);
      } else if (eqOpt === 'a dagger or light hammer') {
        eqListOfOptions.push(['Dagger', 'Light Hammer']);
      } else if (eqOpt === 'A set of dice or deck of cards') {
        eqListOfOptions.push(['Dice Set', 'Playing Card Set']);
      } else if (eqOpt === 'A donkey or mule with bit and bridle') {
        eqListOfOptions.push(['Donkey with bit and bridle', 'Mule with bit and bridle']);
      } else if (eqOpt === 'a set of cold-weather or warm-weather clothes') {
        eqListOfOptions.push(['cold-weather clothes', 'warm-weather clothes']);
      } else if (eqOpt === 'hunting gear (a shortbow with 20 arrows, or a hunting trap)') {
        eqListOfOptions.push(['shortbow with 20 arrows', 'a hunting trap']);
      } else {
        if (eqOpt.startsWith('and ')) {
          eqOpt = eqOpt.substring('and '.length);
        }
        eqListOfOptions.push([eqOpt]);
      }
    })
    eqListOfOptions.forEach(options => {
      if (options.length > 0) {
        equipmentOptions.push({label: background.name, options: options});
        if (options.length === 1) {
          equipmentSelected.push(options[0]);
        } else {
          equipmentSelected.push('');
        }
      }
    })
  }

  static processBackgrounds(backgrounds: Background[]): void {
    backgrounds.forEach((bg) => {
      if (bg.document__slug === 'a5e') {
        const chars = bg.suggested_characteristics || '';
        let rows = chars.split('\n');
        rows[0] = `|d10|${rows[0].replace(/###/g, '').trim()}|`;
        rows[1] = '|---|---|';
        rows = rows.map(o => {
          const loc = o.indexOf('.');
          if (loc > 0 && loc < 5) {
            return '|' + o.substring(0, loc) + '|' + o.substring(loc + 1) + '|';
          }
          return o;
        });
        bg.suggested_characteristics = rows.join('\n');
      }
    });
  }

  static noneBackground: Background = {
    name: 'None', 
    desc: '',
    skill_proficiencies: '',
    tool_proficiencies: '',
    languages: '',
    feature: '',
    feature_desc: '',
    suggested_characteristics: '',
    equipment: '',
    document__license_url: "",
    document__slug: "wotc-srd",
    document__title: "5e Core Rules",
    document__url: ""
  };

  static async fetchBackgrounds(cacheOnly: boolean, progCallback: (data: any[], totalCount: number) => void): Promise<Background[]> {
    try {
      const backgroundObjs: Background[] = await ApiService.getAllBackgrounds(cacheOnly, progCallback);
      const backgrounds: Background[] = [];
      backgrounds.push(this.noneBackground);
      for (const classObj of backgroundObjs) {
        const background: Background = {
          name: '', desc: '', skill_proficiencies: '', tool_proficiencies: '', languages: '',
          feature: '', feature_desc: '', suggested_characteristics: '', equipment: '',
          document__slug: '', document__title: '', document__license_url: '', document__url: ''
        };
        Object.assign(background, classObj);
        backgrounds.push(background);
      }

      this.processBackgrounds(backgrounds);
      return backgrounds;
    } catch (error) {
      console.error("Failed to fetch backgrounds", error);
      return [];
    }
  }
}

export default BackgroundService;
